import { Popover, Typography, theme } from '@/components';
import { type Colors, useColor } from '@/hooks';
import { FALLBACK_TEXT } from '@/utils';
import type { CSSProperties, ComponentProps, FC } from 'react';

export type TextVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'body'
  | 'footnote'
  | 'small';
export type TextColor = Colors;
export type TextWeight = 'regular' | 'medium' | 'bold';
export const TextWeightValue = {
  regular: 400,
  medium: 500,
  bold: 700,
} as const satisfies Record<TextWeight, number>;
const POPOVER_LAYOUT_SIZE_WIDTH = 480;
const POPOVER_LAYOUT_SIZE_HEIGHT = 360;

type Props = {
  text: string | number | null | undefined;
  variant?: TextVariant;
  weight?: TextWeight;
  color?: TextColor;
  fallbackText?: string;
  disabled?: boolean;
  maxWidth?: CSSProperties['maxWidth'];
  maxRows?: number;
  hideTooltip?: boolean;
  withPopover?: boolean;
  popoverTitle?: string;
} & Pick<
  ComponentProps<typeof Typography.Paragraph>,
  'ellipsis' | 'style' | 'className'
>;

export const Text: FC<Props> = ({
  text,
  variant,
  weight,
  color,
  fallbackText = FALLBACK_TEXT,
  style,
  disabled,
  maxWidth,
  maxRows,
  ellipsis,
  className,
  hideTooltip,
  withPopover,
  popoverTitle,
}) => {
  const { token } = theme.useToken();
  const { getColorValue } = useColor();

  let specifiedStyle = {};

  switch (variant) {
    case 'h1': {
      specifiedStyle = {
        fontSize: token.fontSizeHeading1,
        fontWeight: TextWeightValue.medium,
      };
      break;
    }
    case 'h2': {
      specifiedStyle = {
        fontSize: token.fontSizeHeading2,
        fontWeight: TextWeightValue.medium,
      };
      break;
    }
    case 'h3': {
      specifiedStyle = {
        fontSize: token.fontSizeHeading3,
        fontWeight: weight ? TextWeightValue[weight] : TextWeightValue.medium,
      };
      break;
    }
    case 'h4': {
      specifiedStyle = {
        fontSize: token.fontSizeHeading4,
        fontWeight: weight ? TextWeightValue[weight] : TextWeightValue.medium,
      };
      break;
    }
    case 'h5': {
      specifiedStyle = {
        fontSize: token.fontSizeHeading5,
        fontWeight: weight ? TextWeightValue[weight] : TextWeightValue.medium,
      };
      break;
    }
    case 'body': {
      specifiedStyle = {
        fontSize: token.fontSize,
        fontWeight: weight ? TextWeightValue[weight] : TextWeightValue.medium,
      };
      break;
    }
    case 'footnote': {
      specifiedStyle = {
        fontSize: token.fontSizeSM,
        fontWeight: weight ? TextWeightValue[weight] : TextWeightValue.regular,
      };
      break;
    }
    case 'small': {
      specifiedStyle = {
        fontSize: 10,
        fontWeight: weight ? TextWeightValue[weight] : TextWeightValue.medium,
      };
      break;
    }
    default: {
      specifiedStyle = {
        fontSize: token.fontSize,
        fontWeight: weight ? TextWeightValue[weight] : TextWeightValue.medium,
      };
      break;
    }
  }

  const hasLineBreak = typeof text === 'string' && text.includes('\n');

  const typographyStyle: CSSProperties = {
    ...specifiedStyle,
    ...(color && { color: getColorValue(color) }),
    ...(hasLineBreak && {
      whiteSpace: 'pre-wrap',
    }),
    ...(maxWidth && { maxWidth }),
    ...(maxRows && { margin: 0 }),
    ...style,
  };

  const ellipsisConfig =
    (maxWidth ?? maxRows)
      ? {
          tooltip: hideTooltip ? undefined : { overlayStyle: { maxWidth } },
          ...(maxRows && { rows: maxRows }),
          ...(typeof ellipsis === 'object' && ellipsis),
        }
      : ellipsis;

  const displayedText = text?.toString() ?? fallbackText;
  const isEmpty = !text?.toString();

  const renderTypographyParagraph = () => (
    <Typography.Paragraph
      style={typographyStyle}
      disabled={disabled}
      ellipsis={ellipsisConfig}
      className={className}
      {...(isEmpty && { 'data-empty': 'true' })}
    >
      {displayedText}
    </Typography.Paragraph>
  );

  return withPopover ? (
    <Popover
      title={
        <div
          style={{
            maxWidth: POPOVER_LAYOUT_SIZE_WIDTH,
          }}
        >
          {popoverTitle}
        </div>
      }
      content={
        <div
          style={{
            maxWidth: POPOVER_LAYOUT_SIZE_WIDTH,
            maxHeight: POPOVER_LAYOUT_SIZE_HEIGHT,
            overflowY: 'auto',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
          {...(isEmpty && { 'data-empty': 'true' })}
        >
          {displayedText}
        </div>
      }
    >
      {renderTypographyParagraph()}
    </Popover>
  ) : maxRows ? (
    renderTypographyParagraph()
  ) : (
    <Typography.Text
      style={typographyStyle}
      disabled={disabled}
      ellipsis={ellipsisConfig}
      className={className}
      {...(isEmpty && { 'data-empty': 'true' })}
    >
      {displayedText}
    </Typography.Text>
  );
};
